import React from 'react';
import dayjs from 'dayjs';

import { Appointments_Order_By } from '@gql/base-types';
import { useGetMobileServiceAppointmentsQuery, GetMobileServiceAppointmentsQuery } from '../../mobileService/queries/getMobileServiceAppointments.query.generated';
import { useUser, useRooftop } from '@store';

import AppointmentsContent from './AppointmentsContent';

type Appointment = GetMobileServiceAppointmentsQuery['appointments'][number];

const RANGE_KEY = 'appointment-index-range';
const START_KEY = 'appointment-index-start';
const END_KEY = 'appointment-index-end';

const getStartFor = (range: string): string => {
  switch (range) {
    case 'today': return dayjs().startOf('day').toISOString();
    case 'thisWeek': return dayjs().startOf('week').toISOString();
    case 'nextWeek': return dayjs().add(1, 'week').startOf('week').toISOString();
    case 'thisMonth': return dayjs().startOf('month').toISOString();
    case 'thisYear': return dayjs().startOf('year').toISOString();
    default: return dayjs().startOf('month').toISOString();
  }
};

const getEndFor = (range: string): string => {
  switch (range) {
    case 'today': return dayjs().endOf('day').toISOString();
    case 'thisWeek': return dayjs().endOf('week').toISOString();
    case 'nextWeek': return dayjs().add(1, 'week').endOf('week').toISOString();
    case 'thisMonth': return dayjs().endOf('month').toISOString();
    case 'thisYear': return dayjs().endOf('year').toISOString();
    default: return dayjs().endOf('month').toISOString();
  }
};

const getInitialRange = (): string => {
  return localStorage.getItem(RANGE_KEY) || 'thisMonth';
};

const getInitialStart = (range: string): string => {
  if (range === 'custom') {
    return localStorage.getItem(START_KEY) || getStartFor('thisMonth');
  }
  return getStartFor(range);
};

const getInitialEnd = (range: string): string => {
  if (range === 'custom') {
    return localStorage.getItem(END_KEY) || getEndFor('thisMonth');
  }
  return getEndFor(range);
};

const AppointmentsQuery: React.FC = () => {
  const user = useUser();
  const rooftop = useRooftop();

  const customerId = rooftop?.id || user?.profile?.customer_id || 0;

  // Date range
  const [range, setRange] = React.useState<string>(() => getInitialRange());
  const [start, setStart] = React.useState<string>(() => getInitialStart(getInitialRange()));
  const [end, setEnd] = React.useState<string>(() => getInitialEnd(getInitialRange()));
  const [search, setSearch] = React.useState<string>('');

  const handleRangeChange = (value: string) => {
    localStorage.setItem(RANGE_KEY, value);
    setRange(value);
    if (value !== 'custom') {
      setStart(getStartFor(value));
      setEnd(getEndFor(value));
    }
  };

  const handleDateChange = (value: string, name: string) => {
    if (name === 'start') {
      const newDate = dayjs(value).startOf('day').toISOString();
      localStorage.setItem(START_KEY, newDate);
      setStart(newDate);
    } else {
      const newDate = dayjs(value).endOf('day').toISOString();
      localStorage.setItem(END_KEY, newDate);
      setEnd(newDate);
    }
  };

  // Pagination
  const [offset, setOffset] = React.useState(0);
  const [limit, setLimit] = React.useState(50);

  // Sorting
  const [orderBy, setOrderBy] = React.useState('appointment_time');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('desc');

  const orderByGraphQL = React.useMemo<Appointments_Order_By[]>(() => {
    const arr: Appointments_Order_By[] = [];
    if (orderBy) {
      arr.push({ [orderBy]: `${order}_nulls_last` } as any);
    }
    arr.push({ id: 'desc_nulls_last' } as any);
    return arr;
  }, [orderBy, order]);

  const { loading, error, data, refetch } = useGetMobileServiceAppointmentsQuery({
    variables: {
      customerId,
      start,
      end,
      limit,
      offset,
      orderBy: orderByGraphQL,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !customerId,
  });

  // Reset pagination when date range changes
  React.useEffect(() => { setOffset(0); }, [start, end]);

  const appointmentsRaw: Appointment[] = data?.appointments ?? [];
  const totalCount = data?.appointments_aggregate?.aggregate?.count ?? 0;

  // Client-side search filter on top of the server-fetched page
  const appointments: Appointment[] = React.useMemo(() => {
    if (!search.trim()) return appointmentsRaw;
    const q = search.trim().toLowerCase();
    return appointmentsRaw.filter(a => {
      return (
        (a.id != null && String(a.id).includes(q)) ||
        (a.consumer_name && a.consumer_name.toLowerCase().includes(q)) ||
        (a.consumer_phone && a.consumer_phone.toLowerCase().includes(q)) ||
        (a.vehicle_make && a.vehicle_make.toLowerCase().includes(q)) ||
        (a.vehicle_model && a.vehicle_model.toLowerCase().includes(q)) ||
        (a.vehicle_year && String(a.vehicle_year).includes(q)) ||
        (a.vehicle_vin && a.vehicle_vin.toLowerCase().includes(q)) ||
        (a.status && a.status.toLowerCase().includes(q)) ||
        (a.type && a.type.toLowerCase().includes(q))
      );
    });
  }, [appointmentsRaw, search]);

  const handleRefetch = async () => {
    await refetch();
  };

  return (
    <AppointmentsContent
      loading={loading}
      error={error}
      refetch={handleRefetch}
      appointments={appointments}
      totalCount={totalCount}
      offset={offset}
      limit={limit}
      setOffset={setOffset}
      setLimit={setLimit}
      orderBy={orderBy}
      order={order}
      setOrderBy={setOrderBy}
      setOrder={setOrder}
      range={range}
      start={start}
      end={end}
      onRangeChange={handleRangeChange}
      onDateChange={handleDateChange}
      search={search}
      setSearch={setSearch}
    />
  );
};

export default AppointmentsQuery;
